import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Loader2, Search, X } from "lucide-react";
import { fetchUsers } from "../../redux/slices/userSlice";
import { getUserDisplayName } from "../../lib/user-display";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";

/**
 * Search and multi-select users by email or profile name (no UUIDs).
 */
function UserPicker({ selected = [], onChange, disabled = false }) {
  const dispatch = useDispatch();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const selectedIds = new Set(selected.map((u) => u.id));

  const runSearch = useCallback(async () => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setSearched(false);
      return;
    }
    setLoading(true);
    setSearched(true);
    try {
      const data = await dispatch(
        fetchUsers({ page: 1, limit: 20, search: q })
      ).unwrap();
      setResults(data.users || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [dispatch, query]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (query.trim().length >= 2) runSearch();
      else {
        setResults([]);
        setSearched(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [query, runSearch]);

  const toggleUser = (user) => {
    if (disabled) return;
    if (selectedIds.has(user.id)) {
      onChange(selected.filter((u) => u.id !== user.id));
    } else {
      onChange([
        ...selected,
        {
          id: user.id,
          email: user.email,
          label: getUserDisplayName(user),
        },
      ]);
    }
  };

  const removeUser = (id) => {
    if (disabled) return;
    onChange(selected.filter((u) => u.id !== id));
  };

  return (
    <div className="space-y-3">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selected.map((u) => (
            <Badge
              key={u.id}
              variant="secondary"
              className="gap-1 pr-1 py-1.5 text-sm font-normal"
            >
              <span>
                {u.label}
                {u.email ? (
                  <span className="text-muted-foreground"> · {u.email}</span>
                ) : null}
              </span>
              <button
                type="button"
                className="ml-1 rounded-full p-0.5 hover:bg-muted"
                onClick={() => removeUser(u.id)}
                aria-label={`Remove ${u.label}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by name or email…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="pl-10"
          disabled={disabled}
        />
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Searching users…
        </div>
      )}

      {!loading && searched && results.length === 0 && (
        <p className="text-sm text-muted-foreground py-2">
          No users found. Try a different name or email.
        </p>
      )}

      {!loading && results.length > 0 && (
        <div className="rounded-md border divide-y max-h-56 overflow-y-auto">
          {results.map((user) => {
            const checked = selectedIds.has(user.id);
            const name = getUserDisplayName(user);
            return (
              <label
                key={user.id}
                className="flex items-start gap-3 p-3 cursor-pointer hover:bg-muted/50"
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => toggleUser(user)}
                  disabled={disabled}
                  className="mt-0.5"
                />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm truncate">{name}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {user.email}
                  </p>
                </div>
              </label>
            );
          })}
        </div>
      )}

      {query.trim().length > 0 && query.trim().length < 2 && (
        <p className="text-xs text-muted-foreground">
          Type at least 2 characters to search.
        </p>
      )}

      {selected.length === 0 && !query && (
        <p className="text-xs text-muted-foreground">
          Search and tick one or more learners. You never need to copy user IDs.
        </p>
      )}
    </div>
  );
}

export default UserPicker;
